import cornerstoneTools from "cornerstone-tools";
import path from "./annotation/drawing/path";
import { findCircleLineIntersections } from "../Viewers/utils";
import cornerstone from "cornerstone-core";
import { toolStateMap } from "./toolStateManager";
import store from "../../../store";
const BaseAnnotationTool = cornerstoneTools.importInternal(
  "base/BaseAnnotationTool",
);

const external = cornerstoneTools.external;

const draw = cornerstoneTools.importInternal("drawing/draw");
//const drawRect = cornerstoneTools.importInternal('drawing/drawRect');
const setShadow = cornerstoneTools.importInternal("drawing/setShadow");
const getNewContext = cornerstoneTools.importInternal("drawing/getNewContext");
const drawTextBox = cornerstoneTools.importInternal("drawing/drawTextBox");
const drawHandles = cornerstoneTools.importInternal("drawing/drawHandles");
const drawCircle = cornerstoneTools.importInternal("drawing/drawCircle");
const drawLinkedTextBox = cornerstoneTools.importInternal(
  "drawing/drawLinkedTextBox",
);
const drawLine = cornerstoneTools.importInternal("drawing/drawLine");

const getToolState = cornerstoneTools.getToolState;
const toolStyle = cornerstoneTools.toolStyle;
//const textStyle = cornerstoneTools.textStyle;
const toolColors = cornerstoneTools.toolColors;
const getModule = cornerstoneTools.getModule;

//const lineSegDistance = cornerstoneTools.importInternal('util/lineSegDistance');
//const getRGBPixels = cornerstoneTools.importInternal('util/getRGBPixels');
const getPixelSpacing = cornerstoneTools.importInternal("util/getPixelSpacing");
const throttle = cornerstoneTools.importInternal("util/throttle");
const circleRoiCursor = cornerstoneTools.importInternal("cursors/index");
const getROITextBoxCoords = cornerstoneTools.importInternal(
  "util/getROITextBoxCoords",
);
const textStyle = cornerstoneTools.textStyle;
const drawRect = cornerstoneTools.importInternal("drawing/drawRect");

/**
 * @public
 * @class Etdrs
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing circular regions of interest, and measuring
 * the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
let count = 0;

const FLUID_VOLUME_COLORS = {
  IRF: "#FF2B2B",
  PED: "#001BFF",
  SRF: "#1CF504",
  SHRM: "#FFFF00",
};

const ETDRS_FLUID_VOLUME_FONT = "bold 12px Arial";
const ETDRS_VOLUME_FONT = "12px Arial";

// Cardinal ring slots rotated 45° clockwise (same radius, offset from volume labels).
const RING_FLUID_CLOCKWISE_OFFSET_RAD = Math.PI / 4;
const RING_FLUID_POSITIONS = [
  {
    fluid: "PED",
    angleRad: -Math.PI / 2 + RING_FLUID_CLOCKWISE_OFFSET_RAD,
  },
  { fluid: "IRF", angleRad: 0 + RING_FLUID_CLOCKWISE_OFFSET_RAD },
  { fluid: "SRF", angleRad: Math.PI / 2 + RING_FLUID_CLOCKWISE_OFFSET_RAD },
  { fluid: "SHRM", angleRad: Math.PI + RING_FLUID_CLOCKWISE_OFFSET_RAD },
];

const ISF_FLUID_ORDER = ["IRF", "PED", "SHRM", "SRF"];

const getVisibleEtdrsData = (toolData) => {
  if (!toolData?.data?.length) return null;
  return toolData.data.find((d) => d.visible !== false) || toolData.data[0];
};

const distanceFromEtdrsCenterImage = (eventData, centerX, centerY) => {
  if (centerX == null || centerY == null) return Infinity;
  const { x, y } = eventData.currentPoints.image;
  const dx = x - centerX;
  const dy = y - centerY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getRedCircleRadiusImage = (data, eventData) => {
  if (data?.circles_radii?.[0] != null) {
    return data.circles_radii[0];
  }
  const { colPixelSpacing } = getPixelSpacing(eventData.image);
  const scaleX = colPixelSpacing || 1;
  return 1 / scaleX / 2;
};

const isNearEtdrsCenter = (eventData, centerX, centerY, grabRadiusImage) =>
  distanceFromEtdrsCenterImage(eventData, centerX, centerY) <=
  grabRadiusImage;

const formatFluidVolume = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const getFluidVolumeDrawStyle = (fluid) => ({
  color: FLUID_VOLUME_COLORS[fluid],
  font: ETDRS_FLUID_VOLUME_FONT,
});

const drawFluidVolumeTextCanvas = (
  context,
  x,
  y,
  text,
  fluid,
  textAlign = "center",
  textBaseline = "middle",
) => {
  const { color, font } = getFluidVolumeDrawStyle(fluid);
  context.save();
  context.font = font;
  context.fillStyle = color;
  context.textAlign = textAlign;
  context.textBaseline = textBaseline;
  context.fillText(text, x, y);
  context.restore();
};

const getRingLabelPlacementRadius = (
  innerRadiusCanvas,
  outerRadiusCanvas,
  labelText,
  context,
  font = ETDRS_FLUID_VOLUME_FONT,
) => {
  const annulusWidth = Math.max(outerRadiusCanvas - innerRadiusCanvas, 0);
  context.save();
  context.font = font;
  const textWidth = context.measureText(labelText).width;
  context.restore();
  const textHalfExtent = Math.max(textWidth / 2, 7);

  // Keep the label centroid inside the annulus with margin from both boundaries.
  const outerMargin = Math.max(12, textHalfExtent + 4);
  const innerMargin = Math.max(8, textHalfExtent + 2);
  const maxRadius = outerRadiusCanvas - outerMargin;
  const minRadius = innerRadiusCanvas + innerMargin;

  if (maxRadius <= minRadius) {
    return (innerRadiusCanvas + outerRadiusCanvas) / 2;
  }

  // Prefer a position ~30% inward from the outer boundary of this ring band.
  const preferredRadius =
    outerRadiusCanvas - Math.max(outerMargin, annulusWidth * 0.3);

  return Math.min(maxRadius, Math.max(minRadius, preferredRadius));
};

const drawRingFluidVolumes = (
  context,
  element,
  region,
  centerX,
  centerY,
  innerRadiusImage,
  outerRadiusImage,
  viewport,
  activeFluids = null,
) => {
  if (!region) return;

  const scale = viewport?.scale || 1;
  const centerCanvas = external.cornerstone.pixelToCanvas(element, {
    x: centerX,
    y: centerY,
  });
  const innerRadiusCanvas = innerRadiusImage * scale;
  const outerRadiusCanvas = outerRadiusImage * scale;

  RING_FLUID_POSITIONS.forEach(({ fluid, angleRad }) => {
    if (activeFluids && !activeFluids.includes(fluid)) return;
    if (!Object.prototype.hasOwnProperty.call(region, fluid)) return;
    const volume = region[fluid]?.volume_mm3 ?? 0;
    const labelText = formatFluidVolume(volume);
    const drawStyle = getFluidVolumeDrawStyle(fluid);
    const placementRadius = getRingLabelPlacementRadius(
      innerRadiusCanvas,
      outerRadiusCanvas,
      labelText,
      context,
      drawStyle.font,
    );
    const x = centerCanvas.x + placementRadius * Math.cos(angleRad);
    const y = centerCanvas.y + placementRadius * Math.sin(angleRad);
    drawFluidVolumeTextCanvas(
      context,
      x,
      y,
      labelText,
      fluid,
      "center",
      "middle",
    );
  });
};

const drawIsfFluidLegend = (context, element, region, activeFluids = null) => {
  if (!region) return;

  const fluids = ISF_FLUID_ORDER.filter(
    (fluid) =>
      (!activeFluids || activeFluids.includes(fluid)) &&
      Object.prototype.hasOwnProperty.call(region, fluid),
  );
  if (!fluids.length) return;

  const leftCanvasX = 10;
  const centerCanvasY = element.clientHeight / 2;
  const lineHeightCanvas = 18;
  const startY = centerCanvasY - ((fluids.length - 1) * lineHeightCanvas) / 2;

  fluids.forEach((fluid, index) => {
    const volume = region[fluid]?.volume_mm3 ?? 0;
    drawFluidVolumeTextCanvas(
      context,
      leftCanvasX,
      startY + index * lineHeightCanvas,
      formatFluidVolume(volume),
      fluid,
      "left",
      "middle",
    );
  });
};

const getCircleMeanUm = (circleEntry) => {
  if (!circleEntry) return null;
  const mean =
    circleEntry["mean_µm"] ??
    circleEntry.mean_µm ??
    circleEntry.mean_um ??
    circleEntry["mean_um"];
  const n = Number(mean);
  return Number.isFinite(n) ? parseInt(n, 10) : null;
};

const hasCircleDataVolumes = (circleData) =>
  circleData?.CSF?.volume_mm3 != null &&
  circleData?.ISF?.volume_mm3 != null &&
  circleData?.OSF?.volume_mm3 != null;

const drawCircleDataVolumes = (
  context,
  element,
  circleData,
  centerX,
  centerY,
  middleRadius,
  outerRadius,
) => {
  if (!hasCircleDataVolumes(circleData)) return;

  const lineHeight = 14;

  const csfCoords = external.cornerstone.pixelToCanvas(element, {
    x: centerX,
    y: centerY,
  });

  context.save();
  context.font = ETDRS_VOLUME_FONT;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = "white";
  const csfMean = getCircleMeanUm(circleData.CSF);
  if (csfMean != null) {
    context.fillText(csfMean, csfCoords.x, csfCoords.y - lineHeight / 2);
  }
  context.fillText(
    Number(circleData.CSF.volume_mm3).toFixed(2),
    csfCoords.x,
    csfCoords.y + lineHeight / 2,
  );
  context.restore();

  const isfY = centerY - middleRadius + 45;
  const isfCoords = external.cornerstone.pixelToCanvas(element, {
    x: centerX,
    y: isfY,
  });

  context.save();
  context.font = ETDRS_VOLUME_FONT;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = "white";
  const isfMean = getCircleMeanUm(circleData.ISF);
  if (isfMean != null) {
    context.fillText(isfMean, isfCoords.x, isfCoords.y - lineHeight / 2);
  }
  context.fillText(
    Number(circleData.ISF.volume_mm3).toFixed(2),
    isfCoords.x,
    isfCoords.y + lineHeight / 2,
  );
  context.restore();

  const osfY = centerY - outerRadius + 35;
  const osfCoords = external.cornerstone.pixelToCanvas(element, {
    x: centerX,
    y: osfY,
  });

  context.save();
  context.font = ETDRS_VOLUME_FONT;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = "white";
  const osfMean = getCircleMeanUm(circleData.OSF);
  if (osfMean != null) {
    context.fillText(osfMean, osfCoords.x, osfCoords.y - lineHeight / 2);
  }
  context.fillText(
    Number(circleData.OSF.volume_mm3).toFixed(2),
    osfCoords.x,
    osfCoords.y + lineHeight / 2,
  );
  context.restore();
};

export default class Etdrs extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "Etdrs",
    };

    super(props, defaultProps);

    this.isDraggingEtdrs = false;
    this.mouseMoveCallback = this.mouseMoveCallback.bind(this);
    this.mouseDownCallback = this.mouseDownCallback.bind(this);
    this.mouseDragCallback = this.mouseDragCallback.bind(this);
    this.mouseUpCallback = this.mouseUpCallback.bind(this);

    this.mouseEventListeners = [
      {
        eventName: cornerstoneTools.EVENTS.MOUSE_DOWN,
        listener: this.mouseDownCallback,
      },
      {
        eventName: cornerstoneTools.EVENTS.MOUSE_MOVE,
        listener: this.mouseMoveCallback,
      },
      {
        eventName: cornerstoneTools.EVENTS.MOUSE_DRAG,
        listener: this.mouseDragCallback,
      },
      {
        eventName: cornerstoneTools.EVENTS.MOUSE_UP,
        listener: this.mouseUpCallback,
      },
    ];
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return;
    }

    return {
      visible: true,
      active: false,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: false,
          active: false,
          drawnIndependently: false,
          moveable: false,
          locked: true,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: false,
          active: false,
          drawnIndependently: false,
          moveable: false,
          locked: true,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
    };
  }

  updateCachedStats(image, element, data) {
    const seriesModule =
      external.cornerstone.metaData.get("generalSeriesModule", image.imageId) ||
      {};
    const modality = seriesModule.modality;
    const pixelSpacing = getPixelSpacing(image);

    data.invalidated = false;
  }

  renderToolData(evt) {
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const extraState = toolStateMap.get(evt.currentTarget);
    const reduxState = store.getState();
    const targetElement =
      reduxState?.dcm?.dicomRefBscan?.current ||
      extraState?.dicomRefBscan?.current;

    const isElementEnabled = (el) => {
      if (!el) return false;
      try {
        cornerstone.getEnabledElement(el);
        return true;
      } catch (e) {
        return false;
      }
    };

    const getDistance = external.cornerstoneMath.point.distance;
    const eventData = evt.detail;
    const { image, element, canvasContext } = eventData;
    const lineWidth = toolStyle.getToolWidth();
    const {
      handleRadius,
      drawHandlesOnHover,
      hideHandlesIfMoving,
      renderDashed,
      centerPointRadius,
    } = this.configuration;
    const newContext = getNewContext(canvasContext.canvas);
    const { rowPixelSpacing, colPixelSpacing } = getPixelSpacing(image);
    const lineDash = getModule("globalConfiguration").configuration.lineDash;

    // Meta
    const seriesModule =
      external.cornerstone.metaData.get("generalSeriesModule", image.imageId) ||
      {};
    const modality = seriesModule.modality;
    const hasPixelSpacing = rowPixelSpacing && colPixelSpacing;

    let colors = ["red", "orange", "green"];

    // Remove all existing event listeners from target element first
    if (targetElement) {
      // Remove all existing cornerstoneimagerendered listeners
      const oldListeners = targetElement._cornerstoneEventListeners || {};
      if (oldListeners["cornerstoneimagerendered"]) {
        oldListeners["cornerstoneimagerendered"].forEach((listener) => {
          targetElement.removeEventListener(
            "cornerstoneimagerendered",
            listener,
          );
        });
      }

      // Clear tool state and update image only if element is enabled
      if (isElementEnabled(targetElement)) {
        cornerstoneTools.clearToolState(targetElement, "Etdrs");
        cornerstone.updateImage(targetElement);
      }
    }

    draw(newContext, (context) => {
      var viewport = external.cornerstone.getViewport(element);

      // Clear existing tool state first (only if enabled)
      if (isElementEnabled(targetElement)) {
        cornerstoneTools.clearToolState(targetElement, "Etdrs");
        cornerstone.updateImage(targetElement);
      }
      for (let i = 0; i < toolData.data.length; i++) {
        const data = toolData.data[i];
        // console.log("ETDRS data", data);

        if (data.visible === false) {
          continue;
        }

        // Configure
        const showEtdrsMirroring = data?.showEtdrsMirroring || false;
        const showEtdrvalues =
          data?.etdrsvaluesShow !== undefined
            ? data.etdrsvaluesShow
            : Boolean(reduxState?.dcm?.LisonActive);

        setShadow(context, this.configuration);

        // Add visual feedback for active state - thicker lines when moving
        const lineWidth = data.active ? 3 : 1;

        if (
          data?.areas &&
          data?.areas?.ETDRS_area_mm2 !== undefined &&
          showEtdrvalues === true
        ) {
          count++;

          let areas = data?.areas;

          // Set font size and font before measurements
          const fontSize = 12; // adjust this value as needed
          context.font = `${fontSize}px Arial`;

          // Add this helper function inside draw callback
          const measureText = (text) => {
            return context.measureText(text).width;
          };

          // Prepare all text strings first
          const textLines = [
            `⌀1 mm: ${areas.lesions_1mm_area_mm2} mm${String.fromCharCode(
              178,
            )} (${areas.lesions_1mm_percentage}%)`,
            `⌀3 mm: ${areas.lesions_3mm_area_mm2} mm${String.fromCharCode(
              178,
            )} (${areas.lesions_3mm_percentage}%)`,
            `⌀6 mm: ${areas.lesions_6mm_area_mm2} mm${String.fromCharCode(
              178,
            )} (${areas.lesions_6mm_percentage}%)`,
            `ETDRS: ${areas?.ETDRS_area_mm2} mm${String.fromCharCode(178)} (${
              areas?.ETDRS_percentage
            }%)`,
          ];

          // Find the widest text and its pixel width
          const textWidths = textLines.map((text) => measureText(text));
          const maxWidth = Math.max(...textWidths);

          // Calculate text box height
          const lineHeight = 15;

          // const totalHeight = textLines.length * lineHeight;

          // Calculate dynamic padding based on image size
          const basePadding = 0;
          const baseImageSize = 768; // Reference size for scaling
          const scaleFactor = Math.min(image.width / baseImageSize, 2); // Cap the scale factor at 2

          const padding = Math.round(basePadding * scaleFactor);
          const extraRightPadding = Math.round(50 * scaleFactor); // Increased and scaled extra padding

          // Calculate the starting x-coordinate
          // Convert canvas coordinates to image coordinates for proper scaling
          const canvasToImageCoords = external.cornerstone.canvasToPixel(
            element,
            { x: maxWidth, y: 0 },
          );

          const scaledWidth = canvasToImageCoords.x;

          // Calculate coordinates ensuring text stays within image bounds
          const coords = {
            x: Math.max(
              padding,
              image.width - scaledWidth - padding * 1.3 - extraRightPadding,
            ),
            y: image.height - padding,
          };

          // Convert image coordinates to canvas coordinates
          const textCoords = external.cornerstone.pixelToCanvas(
            element,
            coords,
          );

          // Draw each line of text from bottom up
          textLines.forEach((text, index) => {
            drawTextBox(
              context,
              text,
              textCoords.x - 10,
              textCoords.y - (textLines.length - index) * lineHeight - 15,
              "white",
            );
          });
        }
        // Draw the circles using the radius data
        if (data.circles_radii) {
          data.circles_radii.forEach((radii, index) => {
            // Draw circle
            drawCircle(
              context,
              element,
              data.handles.start,
              radii * viewport.scale,
              {
                color: colors[index],
                lineWidth: lineWidth,
                lineDash: [],
              },
              "pixel",
            );
          });

          // Draw ETDRS lines from 1mm circle to 6mm circle
          if (data.circles_radii.length >= 3) {
            const centerX = data.handles.start.x;
            const centerY = data.handles.start.y;
            const innerRadius = data.circles_radii[0]; // 1mm circle (red)
            const middleRadius = data.circles_radii[1]; // 3mm circle (orange)
            const outerRadius = data.circles_radii[2]; // 6mm circle (green)

            const volumesData = data.volumesData || null;
            const volumeRegions = volumesData?.regions || null;
            const circleData = data.circleData || null;
            const hasAnyFluidDirty = Object.values(
              reduxState?.dcm?.fluidDirtyFrames || {},
            ).some(Boolean);
            const activeFluids = reduxState?.dcm?.activeFluidLabels || null;

            if (
              !hasAnyFluidDirty &&
              volumeRegions?.CSF &&
              volumeRegions?.ISF &&
              volumeRegions?.OSF
            ) {
              drawIsfFluidLegend(
                context,
                element,
                volumeRegions.ISF,
                activeFluids,
              );

              drawRingFluidVolumes(
                context,
                element,
                volumeRegions.CSF,
                centerX,
                centerY,
                innerRadius,
                middleRadius,
                viewport,
                activeFluids,
              );
              drawRingFluidVolumes(
                context,
                element,
                volumeRegions.OSF,
                centerX,
                centerY,
                middleRadius,
                outerRadius,
                viewport,
                activeFluids,
              );
            }

            drawCircleDataVolumes(
              context,
              element,
              circleData,
              centerX,
              centerY,
              middleRadius,
              outerRadius,
            );
          }

          data?.circles_radii?.forEach((radii, index) => {
            // Calculate and draw intersection points
            const circle = {
              centerX: data.handles.start.x,
              centerY: data.handles.start.y,
              radius: radii,
            };

            // Get current frame line coordinates
            const frameIndex = data.currentFrame || 0;
            const lineCoords = {
              x1: data.frameLines[frameIndex][0],
              y1: data.frameLines[frameIndex][1],
              x2: data.frameLines[frameIndex][2],
              y2: data.frameLines[frameIndex][3],
            };

            let redLineLength = Math.sqrt(
              (lineCoords.x2 - lineCoords.x1) ** 2 +
                (lineCoords.y2 - lineCoords.y1) ** 2,
            );

            let scalingFactor = data.optWidth / redLineLength;

            // Find intersections
            const intersections = findCircleLineIntersections(
              circle,
              lineCoords,
            );

            // Draw intersection points and vertical lines
            if (intersections && intersections.length > 0) {
              intersections.forEach((point) => {
                let optpoint = (point[0] - lineCoords.x1) * scalingFactor;

                if (
                  targetElement &&
                  cornerstone.getEnabledElement(targetElement)
                ) {
                  // Function to draw the line
                  function drawRedLine(evt) {
                    const eventData = evt.detail;
                    const context = eventData.canvasContext;
                    const element = eventData.element;

                    // Get the settings from dicom_data store

                    // Only draw if showEtdrsMirroring is true
                    if (!showEtdrsMirroring) {
                      return;
                    }

                    // Convert coordinates
                    const start = cornerstone.pixelToCanvas(element, {
                      x: optpoint,
                      y: 0,
                    });
                    const end = cornerstone.pixelToCanvas(element, {
                      x: optpoint,
                      y: data?.optViewerHeight,
                    });

                    // Make sure we draw on top
                    context.save();

                    // Reset transform to ensure correct drawing
                    context.setTransform(1, 0, 0, 1, 0, 0);

                    // Set drawing styles
                    context.beginPath();
                    context.strokeStyle = colors[index];
                    context.lineWidth = 1;
                    context.globalAlpha = 1.0;

                    // Set the dash pattern
                    context.setLineDash([6, 3]);

                    // Draw the line
                    context.moveTo(start.x, start.y);
                    context.lineTo(end.x, end.y);
                    context.stroke();

                    // Reset the dash pattern
                    context.setLineDash([]);

                    context.restore();
                  }

                  // Store listener reference if needed for future cleanup
                  if (!targetElement._cornerstoneEventListeners) {
                    targetElement._cornerstoneEventListeners = {};
                  }
                  if (
                    !targetElement._cornerstoneEventListeners[
                      "cornerstoneimagerendered"
                    ]
                  ) {
                    targetElement._cornerstoneEventListeners[
                      "cornerstoneimagerendered"
                    ] = [];
                  }
                  targetElement._cornerstoneEventListeners[
                    "cornerstoneimagerendered"
                  ].push(drawRedLine);

                  targetElement.addEventListener(
                    "cornerstoneimagerendered",
                    drawRedLine,
                  );
                }

                // Draw vertical line on OPTViewer if dicomRefBscan exists
              });
            }
          });
        }

        // Remove handle drawing completely or set radius to 0
        if (data.handles) {
          data.handles.start.drawnIndependently = false;
          data.handles.end.drawnIndependently = false;
        }

        // Optional: you can comment out drawHandles completely if the above changes don't work
        // drawHandles(context, eventData, data.handles, handleOptions);

        if (data.invalidated === true) {
          if (data.cachedStats) {
            this.throttledUpdateCachedStats(image, element, data);
          } else {
            this.updateCachedStats(image, element, data);
          }
        }
      }
    });
  }

  moveEtdrsToCurrentPoint(evt) {
    const eventData = evt.detail;
    const { element, currentPoints } = eventData;
    const toolData = getToolState(element, this.name);
    if (!toolData) return;

    toolData.data.forEach((data) => {
      if (data.visible === false) return;

      data.handles.start.x = currentPoints.image.x;
      data.handles.start.y = currentPoints.image.y;
      data.handles.end.x = currentPoints.image.x;
      data.handles.end.y = currentPoints.image.y;
      data.handles.start.active = false;
      data.handles.start.highlight = false;
      data.handles.end.active = false;
      data.handles.end.highlight = false;
      data.active = false;
    });

    external.cornerstone.updateImage(element);
  }

  updateEtdrsCursor(evt) {
    const eventData = evt.detail;
    const { element } = eventData;
    const toolData = getToolState(element, this.name);
    const data = getVisibleEtdrsData(toolData);

    if (!data || data.visible === false) {
      element.style.cursor = "default";
      return;
    }

    if (this.isDraggingEtdrs) {
      element.style.cursor = "grabbing";
      return;
    }

    const center = data.handles.start;
    const grabRadiusImage = getRedCircleRadiusImage(data, eventData);
    element.style.cursor = isNearEtdrsCenter(
      eventData,
      center.x,
      center.y,
      grabRadiusImage,
    )
      ? "grab"
      : "default";
  }

  mouseMoveCallback(evt) {
    if (this.isDraggingEtdrs) {
      this.moveEtdrsToCurrentPoint(evt);
      const { element } = evt.detail;
      element.style.cursor = "grabbing";
      return;
    }
    this.updateEtdrsCursor(evt);
  }

  mouseDownCallback(evt) {
    const eventData = evt.detail;
    const { element } = eventData;
    const toolData = getToolState(element, this.name);
    const data = getVisibleEtdrsData(toolData);

    if (!data || data.visible === false) return;

    const center = data.handles.start;
    const grabRadiusImage = getRedCircleRadiusImage(data, eventData);
    if (!isNearEtdrsCenter(eventData, center.x, center.y, grabRadiusImage))
      return;

    this.isDraggingEtdrs = true;
    element.style.cursor = "grabbing";
  }

  mouseDragCallback(evt) {
    if (!this.isDraggingEtdrs) return;
    this.moveEtdrsToCurrentPoint(evt);
  }

  mouseUpCallback(evt) {
    const { element } = evt.detail;
    this.isDraggingEtdrs = false;
    this.updateEtdrsCursor(evt);
  }

  activeCallback(element) {
    // This method is called when the tool is activated
    this.mouseEventListeners.forEach((listener) => {
      element.addEventListener(listener.eventName, listener.listener);
    });
  }

  deactiveCallback(element) {
    // This method is called when the tool is deactivated
    this.isDraggingEtdrs = false;
    element.style.cursor = "default";
    this.mouseEventListeners.forEach((listener) => {
      element.removeEventListener(listener.eventName, listener.listener);
    });
  }
}
